import { FaultData } from "@fairfleet/geotab/types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MAX_FAULT_DATA_PAGES, RESULTS_LIMIT_FAULT_DATA } from "@/constants/resultsLimits";
import { ResultsCapError } from "@/utilities/error/ResultsCapError";
import { fetchAllSortedByDate } from "@/utilities/query/fetchAllSortedByDate";

/**
 * Hook to fetch the latest fault data for multiple devices within a date range.
 * Supports optional fault source filtering, a result limit, and active fault filtering.
 * Fault data is aggregated by unique device-diagnostic combinations and by default refetched every
 * 30 seconds while the tab is visible.
 *
 * @param deviceIds - Array of device IDs to fetch fault data for.
 * @param fromDate - Start date for the fault data query.
 * @param toDate - End date for the fault data query.
 * @param faultSources - Optional array of fault source IDs to filter results.
 * @param limit - Results per request (default `RESULTS_LIMIT_FAULT_DATA`). Without `rethrowErrors` it caps
 *   the whole result; with `rethrowErrors` it is the page size, and up to `MAX_FAULT_DATA_PAGES` pages
 *   are read.
 * @param onlyActive - Whether to fetch only active faults (default is true).
 * @param enabled - False holds the query back (e.g. until a persisted date range has loaded; default is true).
 * @param rethrowErrors - Reject the query when a FaultData call fails instead of treating it as no faults (default is false).
 *   Full pages are then paged by date up to `MAX_FAULT_DATA_PAGES`; if the last one is still full the
 *   query rejects with a {@link ResultsCapError}, because the result may be incomplete.
 * @param refetchInterval - Poll interval in ms, or false for no polling (default is 30000).
 *
 * @returns Aggregated fault data as an array of FaultData.
 */
export function useLatestFaultData(
  deviceIds: string[],
  fromDate: Date,
  toDate: Date,
  faultSources?: string[],
  limit?: number,
  onlyActive = true,
  enabled = true,
  rethrowErrors = false,
  refetchInterval: number | false = 30000,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["faultData", activeSessionId, deviceIds, fromDate, toDate, faultSources, onlyActive, rethrowErrors],
    refetchInterval,
    placeholderData: keepPreviousData,
    enabled: deviceIds?.length > 0 && enabled,
    // A full page is deterministic; retrying would only download the same faults again.
    retry: (failureCount, error) => !(error instanceof ResultsCapError) && failureCount < 3,
    queryFn: async ({ signal }): Promise<FaultData[]> => {
      const pageSize = limit ?? RESULTS_LIMIT_FAULT_DATA;
      const results = await Promise.all(
        ((faultSources?.length ?? 0) > 0 ? (faultSources ?? []) : [null]).map(async sourceId => {
          const searchParams = {
            fromDate,
            toDate,
            deviceSearch: { deviceIds },
            ...(sourceId !== null && {
              diagnosticSearch: {
                sourceSearch: { id: sourceId },
              },
            }),
            ...(onlyActive && { state: "Active" }),
          };

          try {
            if (rethrowErrors) {
              const { items, complete } = await fetchAllSortedByDate<FaultData>(
                (params, pageSignal) => geotabClient.call<FaultData[]>("Get", params, pageSignal),
                { typeName: "FaultData", search: searchParams, pageSize, maxPages: MAX_FAULT_DATA_PAGES, signal },
              );
              if (!complete) {
                throw new ResultsCapError("FaultData", pageSize * MAX_FAULT_DATA_PAGES);
              }
              return items;
            }

            const faults = await geotabClient.get("FaultData", searchParams, pageSize, signal);
            return faults || [];
          } catch (error) {
            if (rethrowErrors) throw error;
            return [];
          }
        }),
      );

      // Combine results from all fault sources
      const aggregatedFaults = results.flat().reduce((acc: { [key: string]: FaultData }, fault) => {
        const uniqueKey = `${fault.device.id}-${fault.diagnostic.id}`; // Unique key per device-diagnostic pair

        if (!acc[uniqueKey]) {
          acc[uniqueKey] = { ...fault, count: 0 };
        }

        acc[uniqueKey].dateTime =
          acc[uniqueKey].dateTime > fault.dateTime ? acc[uniqueKey].dateTime : fault.dateTime;
        acc[uniqueKey].count += fault.count;

        return acc;
      }, {});

      return Object.values(aggregatedFaults ?? {});
    },
  });
}
